import { useState } from "react";
import { Menu, X, Bot, MessageCircle, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useSmoothScroll } from "@/hooks/useSmoothScroll";
import { useHomeDialogs } from "@/hooks/useHomeDialogs";
import { SERVICES } from "@/data/services";
import { WHATSAPP_LINK } from "@/lib/contact";
import { GiulIATeckSection } from "@/components/GiulIATeckSection";
import { QuoteModal } from "@/components/QuoteModal";

const ACCENT = "#6366F1";
const GLOW = "rgba(99,102,241,0.30)";
const DEMO_PREFILL = "Bonjour Pépé, je souhaite une démo de Giulia (GiulIATeck) pour mon entreprise.";

const GiuliaNav = ({ onDemo }) => {
  const [open, setOpen] = useState(false);
  return (
    <header className="fixed top-0 inset-x-0 z-50 glass border-b border-line" data-testid="giulia-nav">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/giulia" className="flex items-center gap-2.5 group" data-testid="giulia-logo">
          <span className="grid place-items-center w-9 h-9 rounded-xl" style={{ background: GLOW, color: "#c7d2fe" }}><Bot size={20} /></span>
          <span className="leading-none"><span className="font-display text-xl tracking-wide">Giul<span style={{ color: "#a5b4fc" }}>IA</span>Teck</span><span className="block font-mono-ui text-[9px] uppercase tracking-[0.25em] text-slate-500 mt-0.5">une marque PEPE AVS</span></span>
        </Link>
        <nav className="hidden sm:flex items-center gap-5">
          <Link to="/" data-testid="giulia-nav-pepeavs" className="font-mono-ui text-xs uppercase tracking-widest text-slate-300 hover:text-cyan-300 transition-colors inline-flex items-center gap-1">PEPE AVS<ArrowUpRight size={13} /></Link>
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="giulia-nav-whatsapp" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#10B981] text-ink font-bold text-sm hover:scale-105 transition-transform"><MessageCircle size={14} />WhatsApp</a>
          <button onClick={onDemo} data-testid="giulia-nav-demo" className="px-4 py-2 rounded-full text-white font-bold text-sm hover:scale-105 transition-transform" style={{ background: ACCENT, boxShadow: `0 0 20px ${GLOW}` }}>Demander une démo</button>
        </nav>
        <button className="sm:hidden p-2 text-slate-200" onClick={() => setOpen((v) => !v)} aria-label="Menu" data-testid="giulia-nav-toggle">{open ? <X size={24} /> : <Menu size={24} />}</button>
      </div>
      {open && (
        <div className="sm:hidden px-5 pb-4 flex flex-col gap-3 glass border-t border-line" data-testid="giulia-mobile-menu">
          <Link to="/" className="font-mono-ui text-sm uppercase tracking-widest text-slate-200 py-2">← PEPE AVS</Link>
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="px-4 py-3 rounded-full bg-[#10B981] text-ink font-bold text-center">WhatsApp</a>
          <button onClick={onDemo} className="px-4 py-3 rounded-full text-white font-bold text-center" style={{ background: ACCENT }}>Demander une démo</button>
        </div>
      )}
    </header>
  );
};

const GiuliaFooter = () => (
  <footer className="border-t border-line py-10 text-center px-5" data-testid="giulia-footer">
    <p className="font-display text-2xl tracking-wide">Giul<span style={{ color: "#a5b4fc" }}>IA</span>Teck</p>
    <p className="text-sm text-slate-400 mt-2">L'IA qui travaille pour ton entreprise — une marque de PEPE AVS, Rosny-sous-Bois.</p>
    <div className="mt-5 flex items-center justify-center gap-4">
      <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-bold" style={{ color: "#c7d2fe" }}><MessageCircle size={15} />WhatsApp</a>
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-300 hover:text-cyan-300 transition-colors">PEPE AVS<ArrowUpRight size={13} /></Link>
    </div>
    <p className="mt-6 font-mono-ui text-[10px] uppercase tracking-widest text-slate-600">© PEPE AVS · GiulIATeck</p>
  </footer>
);

export default function GiuliaPage() {
  useSmoothScroll();
  const { quote, openQuote, closeQuote } = useHomeDialogs();
  const service = SERVICES.find((s) => s.id === "ia-digital") || null;
  return (
    <div className="relative min-h-screen bg-ink text-slate-50 antialiased">
      <div className="grain" aria-hidden="true" />
      <GiuliaNav onDemo={() => openQuote(service, DEMO_PREFILL)} />
      <main className="pt-16">
        <GiulIATeckSection service={service} onQuote={openQuote} />
      </main>
      <GiuliaFooter />
      <QuoteModal open={quote.open} service={quote.service} prefillMessage={quote.prefill} onClose={closeQuote} onSent={() => {}} />
    </div>
  );
}
